import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";
import { products } from "@/mocks/products";
import { getRecentArticles } from "@/mocks/articles";
import { faqItems } from "@/mocks/faq";

const CATEGORY_IMAGES: Record<string, string> = {
  "industrial-connectors": "https://readdy.ai/api/search-image?query=Heavy-duty%20industrial%20electrical%20connectors%20with%20rugged%20metal%20housing%2C%20waterproof%20sealing%2C%20professional%20workshop%20background%20with%20machinery%2C%20clean%20product%20photography%2C%20warm%20neutral%20lighting%2C%20high%20detail&width=600&height=400&seq=cat-industrial-connectors&orientation=landscape",
  "household-connectors": "https://readdy.ai/api/search-image?query=Modern%20household%20electrical%20plug%20and%20socket%20connectors%2C%20minimalist%20white%20design%2C%20clean%20home%20interior%20background%2C%20soft%20natural%20light%2C%20lifestyle%20product%20photography%2C%20warm%20tones&width=600&height=400&seq=cat-household-connectors&orientation=landscape",
  "construction-connectors": "https://readdy.ai/api/search-image?query=Professional%20construction%20site%20electrical%20connectors%2C%20durable%20heavy-duty%20plugs%20and%20sockets%2C%20orange%20and%20black%20industrial%20design%2C%20construction%20background%20with%20tools%2C%20bright%20clear%20lighting%2C%20sharp%20detail&width=600&height=400&seq=cat-construction-connectors&orientation=landscape",
  "precision-connectors": "https://readdy.ai/api/search-image?query=Precision%20miniature%20electronic%20connectors%20with%20gold-plated%20contacts%2C%20laboratory%20clean%20room%20background%2C%20macro%20photography%20style%2C%20soft%20diffused%20lighting%2C%20technical%20aesthetic%2C%20high%20detail&width=600&height=400&seq=cat-precision-connectors&orientation=landscape",
  "waterproof-series": "https://readdy.ai/api/search-image?query=IP68%20waterproof%20electrical%20connectors%20with%20blue%20rubber%20seals%2C%20outdoor%20rainy%20environment%20background%2C%20water%20droplets%20on%20connector%20surface%2C%20dramatic%20lighting%2C%20professional%20product%20photography%2C%20clean%20composition&width=600&height=400&seq=cat-waterproof-series&orientation=landscape",
  "custom-solutions": "https://readdy.ai/api/search-image?query=Custom%20engineered%20electrical%20connector%20prototypes%20on%20technical%20drawing%20blueprints%2C%20engineering%20office%20background%2C%20warm%20desk%20lamp%20lighting%2C%20professional%20industrial%20design%20photography%2C%20detailed%20macro%20view&width=600&height=400&seq=cat-custom-solutions&orientation=landscape",
};

const PRODUCT_IMAGES: Record<string, string> = {
  "jj-hd-001": "https://readdy.ai/api/search-image?query=Heavy-duty%20industrial%20power%20connector%20HD-001%2C%20black%20and%20orange%20rugged%20design%2C%20metal%20contacts%20visible%2C%20workshop%20background%2C%20warm%20lighting%2C%20high%20contrast%20product%20photography&width=600&height=480&seq=prod-jj-hd-001&orientation=landscape",
  "jj-hd-002": "https://readdy.ai/api/search-image?query=Modular%20industrial%20rectangular%20connector%20with%20multiple%20pins%2C%20gray%20and%20black%20housing%2C%20DIN%20rail%20mounting%2C%20professional%20studio%20lighting%2C%20clean%20white%20background%2C%20technical%20product%20style&width=600&height=480&seq=prod-jj-hd-002&orientation=landscape",
  "jj-hd-003": "https://readdy.ai/api/search-image?query=Large%20circular%20industrial%20power%20connector%20with%20silver%20contacts%2C%20metal%20body%20with%20blue%20trim%2C%20industrial%20power%20distribution%20background%2C%20dramatic%20side%20lighting%2C%20professional%20product%20shot&width=600&height=480&seq=prod-jj-hd-003&orientation=landscape",
  "jj-hh-001": "https://readdy.ai/api/search-image?query=Modern%20white%20household%20power%20strip%20with%20safety%20shutters%2C%20clean%20home%20desk%20background%2C%20soft%20natural%20window%20light%2C%20minimalist%20lifestyle%20photography%2C%20warm%20cozy%20atmosphere&width=600&height=480&seq=prod-jj-hh-001&orientation=landscape",
  "jj-hh-002": "https://readdy.ai/api/search-image?query=Sleek%20white%20wall%20power%20outlet%20with%20USB-C%20ports%2C%20modern%20interior%20wall%20background%2C%20side%20angle%2C%20soft%20natural%20lighting%2C%20clean%20product%20photography%2C%20architectural%20style&width=600&height=480&seq=prod-jj-hh-002&orientation=landscape",
  "jj-hh-003": "https://readdy.ai/api/search-image?query=Smart%20WiFi%20plug%20socket%2C%20white%20minimalist%20design%20with%20LED%20indicator%2C%20modern%20smart%20home%20background%2C%20warm%20ambient%20lighting%2C%20tech%20lifestyle%20product%20photography%2C%20clean%20and%20bright&width=600&height=480&seq=prod-jj-hh-003&orientation=landscape",
  "jj-sc-001": "https://readdy.ai/api/search-image?query=Explosion-proof%20industrial%20connector%2C%20cast%20aluminum%20housing%20in%20dark%20gray%2C%20mining%20site%20background%2C%20dramatic%20directional%20lighting%2C%20rugged%20professional%20product%20photography&width=600&height=480&seq=prod-jj-sc-001&orientation=landscape",
  "jj-sc-002": "https://readdy.ai/api/search-image?query=Portable%20construction%20site%20power%20distribution%20box%2C%20yellow%20and%20black%20industrial%20design%2C%20outdoor%20construction%20site%20background%2C%20bright%20daylight%2C%20rugged%20product%20photography&width=600&height=480&seq=prod-jj-sc-002&orientation=landscape",
  "jj-pc-001": "https://readdy.ai/api/search-image?query=Miniature%20precision%20electronic%20connector%20with%20gold-plated%20pins%20on%20PCB%2C%20laboratory%20clean%20background%2C%20macro%20photography%2C%20soft%20diffused%20lighting%2C%20high-end%20technical%20aesthetic&width=600&height=480&seq=prod-jj-pc-001&orientation=landscape",
  "jj-pc-002": "https://readdy.ai/api/search-image?query=High-frequency%20coaxial%20RF%20connector%20with%20gold-plated%20SMA%20interface%2C%20technical%20laboratory%20background%20with%20test%20equipment%2C%20precise%20clean%20lighting%2C%20scientific%20product%20photography&width=600&height=480&seq=prod-jj-pc-002&orientation=landscape",
  "jj-wp-001": "https://readdy.ai/api/search-image?query=Outdoor%20waterproof%20electrical%20connector%20with%20blue%20rubber%20sealing%20ring%2C%20garden%20fountain%20background%20with%20water%20splashes%2C%20bright%20natural%20light%2C%20professional%20outdoor%20product%20photography&width=600&height=480&seq=prod-jj-wp-001&orientation=landscape",
  "jj-wp-002": "https://readdy.ai/api/search-image?query=Stainless%20steel%20underwater%20connector%20with%20dual%20O-ring%20seals%2C%20underwater%20blue%20environment%20background%20with%20bubbles%2C%20dramatic%20lighting%20through%20water%2C%20professional%20technical%20photography&width=600&height=480&seq=prod-jj-wp-002&orientation=landscape",
  "jj-wp-003": "https://readdy.ai/api/search-image?query=Quick-lock%20waterproof%20plug%20connector%2C%20blue%20and%20black%20design%2C%20outdoor%20camping%20tent%20background%2C%20warm%20sunset%20lighting%2C%20lifestyle%20adventure%20product%20photography&width=600&height=480&seq=prod-jj-wp-003&orientation=landscape",
  "jj-cs-001": "https://readdy.ai/api/search-image?query=Custom%20multi-pin%20hybrid%20connector%20assembly%20on%20engineering%20workbench%2C%20technical%20drawings%20and%203D%20models%20in%20background%2C%20warm%20desk%20lamp%2C%20professional%20industrial%20design%20photography&width=600&height=480&seq=prod-jj-cs-001&orientation=landscape",
};

const ARTICLE_IMAGES: Record<string, string> = {
  "guide-industrial-connector-selection": "https://readdy.ai/api/search-image?query=Professional%20engineer%20selecting%20industrial%20connectors%20from%20a%20display%20board%2C%20clean%20workshop%20background%2C%20warm%20neutral%20lighting%2C%20focused%20composition%2C%20technical%20B2B%20photography%20style&width=600&height=440&seq=art-guide-industrial&orientation=landscape",
  "waterproof-connector-application": "https://readdy.ai/api/search-image?query=Outdoor%20LED%20landscape%20lighting%20installation%20with%20waterproof%20connectors%2C%20garden%20at%20dusk%2C%20warm%20golden%20hour%20lighting%2C%20professional%20outdoor%20engineering%20photography%2C%20detailed%20connection%20points%20visible&width=600&height=440&seq=art-waterproof-app&orientation=landscape",
  "smart-home-connector-trends": "https://readdy.ai/api/search-image?query=Modern%20smart%20home%20living%20room%20with%20smart%20plugs%20and%20connected%20devices%2C%20clean%20interior%20design%2C%20warm%20ambient%20lighting%2C%20lifestyle%20technology%20photography%2C%20cozy%20modern%20atmosphere&width=600&height=440&seq=art-smart-home&orientation=landscape",
  "connector-maintenance-guide": "https://readdy.ai/api/search-image?query=Technician%20cleaning%20and%20inspecting%20electrical%20connectors%20at%20workbench%2C%20tools%20and%20cleaning%20supplies%20visible%2C%20workshop%20environment%2C%20bright%20task%20lighting%2C%20professional%20maintenance%20photography&width=600&height=440&seq=art-maintenance&orientation=landscape",
  "custom-connector-development": "https://readdy.ai/api/search-image?query=Engineering%20team%20discussing%20custom%20connector%20design%20with%203D%20CAD%20models%20on%20large%20display%20screen%2C%20modern%20R&width=600&height=440&seq=art-custom-dev&orientation=landscape",
  "construction-site-safety": "https://readdy.ai/api/search-image?query=Construction%20site%20electrical%20safety%20setup%20with%20distribution%20box%20and%20protected%20connectors%2C%20workers%20in%20safety%20gear%2C%20bright%20outdoor%20lighting%2C%20professional%20industrial%20safety%20photography%2C%20organized%20cable%20management&width=600&height=440&seq=art-construction-safety&orientation=landscape",
  "connector-quality-standards": "https://readdy.ai/api/search-image?query=Quality%20control%20laboratory%20with%20testing%20equipment%20for%20electrical%20connectors%2C%20technicians%20in%20white%20coats%20inspecting%20products%2C%20clean%20bright%20laboratory%20lighting%2C%20professional%20industrial%20photography%2C%20precision%20instruments%20visible&width=600&height=440&seq=art-quality&orientation=landscape",
  "future-of-connector-technology": "https://readdy.ai/api/search-image?query=Futuristic%20concept%20of%20smart%20connectors%20with%20glowing%20data%20visualization%2C%20clean%20tech%20laboratory%20background%2C%20warm%20orange%20accent%20lighting%2C%20innovation%20technology%20photography%2C%20abstract%20digital%20patterns&width=600&height=440&seq=art-future-tech&orientation=landscape",
};

function ProductCard({ product }: { product: (typeof products)[0] }) {
  const { t } = useTranslation();
  const category = categories.find((c) => c.id === product.category);
  return (
    <Link
      to={`/category/${product.category}`}
      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={PRODUCT_IMAGES[product.id]}
          alt={product.name}
          title={`${product.name} - 久久插持久插接解决方案`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {category && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 backdrop-blur-sm text-foreground-700">
            {category.name}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {product.name}
        </h3>
        <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {product.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-primary-600">
            {product.price}
          </span>
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            {t("products.viewDetail")}
            <i className="ri-arrow-right-line text-xs"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}

function CategoryCard({ category }: { category: (typeof categories)[0] }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/category/${category.slug}`}
      className="group relative bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200 h-full"
    >
      <div className="relative h-36 overflow-hidden">
        <img
          src={CATEGORY_IMAGES[category.id]}
          alt={category.name}
          title={`${category.name} - 久久插持久插接解决方案`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
          {category.name}
        </h3>
        <p className="text-xs text-foreground-500">
          {category.productCount} {t("category.products")}
        </p>
      </div>
    </Link>
  );
}

function ArticleCard({
  article,
}: {
  article: ReturnType<typeof getRecentArticles>[0];
}) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200"
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={ARTICLE_IMAGES[article.id]}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500/90 text-white">
          {article.category}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
          <span>{article.date}</span>
          <span>{article.readTime}</span>
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
      </div>
    </Link>
  );
}

function FaqItem({
  item,
  isOpen,
  onToggle,
}: {
  item: (typeof faqItems)[0];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden transition-all duration-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
      >
        <span className="text-sm font-medium text-foreground-900 pr-4">
          {item.question}
        </span>
        <span
          className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <i className="ri-arrow-down-s-line text-foreground-500"></i>
        </span>
      </button>
      {isOpen && (
        <div className="px-5 pb-4">
          <p className="text-sm text-foreground-600 leading-relaxed">
            {item.answer}
          </p>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredProducts = useMemo(() => products.slice(0, 4), []);
  const recentArticles = useMemo(() => getRecentArticles(3), []);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20industrial%20workshop%20with%20organized%20electrical%20connectors%20and%20tools%20on%20display%2C%20warm%20amber%20and%20copper%20accents%2C%20clean%20modern%20manufacturing%20environment%2C%20soft%20dramatic%20lighting%20from%20large%20windows%2C%20high-end%20industrial%20photography%2C%20warm%20neutral%20tones%20with%20metallic%20highlights&width=1800&height=1300&seq=hero-bg-jiujiu&orientation=landscape"
            alt="久久插工业插接解决方案"
            title="久久插——持久稳固的插接解决方案"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-5 leading-tight">
              {t("hero.title")}
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/category/industrial-connectors"
                className="px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                {t("hero.cta")}
              </Link>
              <Link
                to="/about"
                className="px-7 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-medium cursor-pointer hover:bg-white/25 transition-all duration-200 whitespace-nowrap"
              >
                {t("hero.secondary")}
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {t("categories.title")}
              </h2>
              <p className="text-sm text-foreground-600">
                {t("categories.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <CategoryCard key={cat.id} category={cat} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Products */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {t("products.featured")}
                </h2>
                <p className="text-sm text-foreground-600">
                  {t("products.featured.subtitle")}
                </p>
              </div>
              <Link
                to="/category/industrial-connectors"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                {t("categories.viewAll")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {t("advantages.title")}
              </h2>
              <p className="text-sm text-foreground-600">
                {t("advantages.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: "ri-medal-line",
                  title: t("advantages.quality"),
                  desc: t("advantages.quality.desc"),
                  color: "accent",
                },
                {
                  icon: "ri-timer-flash-line",
                  title: t("advantages.durability"),
                  desc: t("advantages.durability.desc"),
                  color: "primary",
                },
                {
                  icon: "ri-shield-check-line",
                  title: t("advantages.protection"),
                  desc: t("advantages.protection.desc"),
                  color: "secondary",
                },
                {
                  icon: "ri-tools-line",
                  title: t("advantages.custom"),
                  desc: t("advantages.custom.desc"),
                  color: "accent",
                },
                {
                  icon: "ri-safe-line",
                  title: t("advantages.safety"),
                  desc: t("advantages.safety.desc"),
                  color: "primary",
                },
                {
                  icon: "ri-customer-service-2-line",
                  title: t("advantages.support"),
                  desc: t("advantages.support.desc"),
                  color: "secondary",
                },
              ].map((adv, idx) => {
                const colorMap: Record<string, string> = {
                  primary:
                    "bg-primary-100 text-primary-700 group-hover:bg-primary-500 group-hover:text-white",
                  accent:
                    "bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-white",
                  secondary:
                    "bg-secondary-100 text-secondary-700 group-hover:bg-secondary-500 group-hover:text-white",
                };
                return (
                  <div
                    key={idx}
                    className="group flex gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70 cursor-default transition-all duration-300 hover:border-primary-200"
                  >
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${colorMap[adv.color]}`}
                    >
                      <i className={`${adv.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">
                        {adv.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        {adv.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {t("articles.latest")}
                </h2>
                <p className="text-sm text-foreground-600">
                  {t("articles.latest.subtitle")}
                </p>
              </div>
              <Link
                to="/articles"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                {t("articles.viewAll")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recentArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {t("faq.title")}
              </h2>
              <p className="text-sm text-foreground-600">{t("faq.subtitle")}</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, idx) => (
                <FaqItem
                  key={idx}
                  item={item}
                  isOpen={openFaq === idx}
                  onToggle={() =>
                    setOpenFaq(openFaq === idx ? null : idx)
                  }
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20industrial%20workspace%20with%20copper%20and%20brass%20connectors%20neatly%20arranged%20on%20wooden%20workbench%2C%20soft%20warm%20lighting%20from%20above%2C%20professional%20craftsmanship%20atmosphere%2C%20rich%20amber%20and%20golden%20tones%2C%20high%20quality%20product%20photography%20with%20shallow%20depth%20of%20field&width=1800&height=800&seq=cta-jiujiu&orientation=landscape"
            alt="久久插专业服务"
            title="久久插——为您提供专业插接解决方案"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
          <div className="relative z-10 w-full max-w-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-white mb-4">
              {t("cta.title")}
            </h2>
            <p className="text-sm text-white/75 mb-8">{t("cta.subtitle")}</p>
            <Link
              to="/about"
              className="inline-flex px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              {t("cta.button")}
            </Link>
          </div>
        </section>
      </main>

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "久久插",
            url: "https://www.jiujiucha.com/",
            description:
              "久久插，专为追求持久稳固插接效果的用户打造。工业连接、家用装配、专业施工，提供经久耐用的插接体验。",
            potentialAction: {
              "@type": "SearchAction",
              target:
                "https://www.jiujiucha.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
            publisher: {
              "@type": "Organization",
              name: "久久插",
              url: "https://www.jiujiucha.com/",
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+86-400-888-6688",
                contactType: "customer service",
                availableLanguage: ["Chinese"],
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          }),
        }}
      />

      <Footer />
    </>
  );
}